// @ts-check
/// <reference path="../../types/index.js" />

import React, { useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { useHistory } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import DialogActions from '@mui/material/DialogActions';
import FormUpdatedContext from '../../contexts/FormUpdatedContext';
import SaveWindowDataContext from '../../contexts/SaveWindowDataContext';
import ResetWindowDataContext from '../../contexts/ResetWindowDataContext';
import { Box, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';

/**
 * Represents a modal dialog with options to save or discard changes.
 * @typedef {Object} SaveAllExitDialog
 * @typedef {string} nextPath - The path the user is navigating to
 * @property {boolean} open - Whether the dialog is open or not.
 * @property {() => void} handleClose - A function to close the dialog.
 */
const CustomDialog = styled(Dialog)(({ theme }) => ({
  '.MuiDialog-paper': {
    height: '385px',
    padding: '30px 40px',
  },
}));

/**
 * A modal dialog component that offers save or discard options for multiple windows.
 * @param {SaveAllExitDialog} props - The properties passed to the SaveAllDialog component.
 * @return {JSX.Element} The SaveAllDialog component.
 */
const SaveAllExitDialog = ({ open, nextPath, handleClose }) => {
  /**
   * @type {FormUpdatedContextType}
   */
  const { resetFormCode } = useContext(FormUpdatedContext);

  /**
   * @type {SaveWindowDataContextType}
   */
  const { triggerAllSaves } = useContext(SaveWindowDataContext);

  /**
   * @type {ResetWindowDataContextType}
   */
  const { triggerAllResets } = useContext(ResetWindowDataContext);

  const history = useHistory();

  /**
   * Saves all changes on windows that were updated
   * @return {Promise<void>}
   */
  const handleSaveChanges = async () => {
    await triggerAllSaves();
    await resetFormCode();
    await handleClose();
    history.push(nextPath);
  };

  /**
   * Saves all changes on windows that were updated
   * @return {Promise<void>}
   */
  const handleResetChanges = async () => {
    await triggerAllResets();
    await resetFormCode();
    await handleClose();
    history.push(nextPath);
  };

  return (
    <CustomDialog
      onClick={(event) => event.preventDefault()}
      disableEscapeKeyDown={true}
      onClose={handleClose}
      open={open}
      fullWidth
    >
      <DialogTitle id="unsaved-changes-dialog-title" sx={{ position: 'relative' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Unsaved Changes
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: '-36px', top: '-28px' }}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography>
          You have made changes that will need to be saved or discarded before leaving the page.
        </Typography>
        <Typography
          sx={{
            marginTop: '28px',
          }}
        >
          Do you want to discard your changes or save them?
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          width: '100%',
          justifyContent: 'end',
        }}
      >
        <Button variant="outlined" color="info" onClick={handleClose} className="start">
          Cancel
        </Button>
        <Box flex="1" />
        <Button variant="outlined" color="error" onClick={handleResetChanges}>
          Discard All
        </Button>
        <Button variant="contained" onClick={handleSaveChanges}>
          Save All
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default SaveAllExitDialog;
